/**
 * CREATIVE DETONATOR - Auth UI Components
 *
 * Handles header user menu, login/logout buttons, and credit display.
 * This module should be imported by pages that need auth UI integration.
 *
 * LIFECYCLE: This module waits for the `layout:header-injected` event if
 * the header is injected dynamically. For pages with static headers, it
 * initializes immediately.
 *
 * REFACTOR-03: Uses centralized event bus for auth events
 */

import { authState } from './state.js';
import { signOut } from './client.js';
// REFACTOR-03: Import Events for centralized event handling
import { subscribe, emit } from '../../../js/core/events.js';
import { AUTH_EVENTS } from '../../../js/core/event-types.js';

// ============================================================================
// DOM ELEMENTS
// ============================================================================

let userMenu = null;
let userMenuTrigger = null;
let profileWidget = null;
let profileTrigger = null;
let userNameEl = null;
let userCreditsEl = null;
let loginLink = null;
let logoutBtn = null;
let dashboardLinks = null; // All dashboard nav links
let generateLinks = null;  // Phase 1: All generate/CTA links that require auth
let footerLoginLink = null; // Phase 1: Footer login link

// ============================================================================
// LOCAL STORAGE CACHE (prevents jumpy UI between pages)
// ============================================================================

const AUTH_CACHE_KEY = 'cd_auth_ui_cache';

function getCachedAuthState() {
  try {
    const cached = localStorage.getItem(AUTH_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    console.warn('[AuthUI] Failed to read cache:', e);
  }
  return null;
}

function setCachedAuthState(isLoggedIn, displayName, credits) {
  try {
    localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify({
      isLoggedIn,
      displayName,
      credits,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.warn('[AuthUI] Failed to write cache:', e);
  }
}

function clearCachedAuthState() {
  try {
    localStorage.removeItem(AUTH_CACHE_KEY);
  } catch (e) {
    // ignore
  }
}

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Wait for header to be injected if using dynamic layout
 * @returns {Promise<void>}
 */
async function waitForHeader() {
  // Check if header already exists (static HTML or already injected)
  if (document.querySelector('.header')) {
    return;
  }

  // Check if we have a placeholder for dynamic header
  const headerPlaceholder = document.getElementById('global-header');
  if (!headerPlaceholder) {
    // No placeholder means this page doesn't use dynamic headers
    return;
  }

  // Wait for header injection event
  console.log('[AuthUI] Waiting for header injection...');
  await new Promise(resolve => {
    window.addEventListener('layout:header-injected', resolve, { once: true });
  });
  console.log('[AuthUI] Header injection complete, proceeding with init');
}

/**
 * Wait for footer to be injected if using dynamic layout
 * Phase 1: Footer contains login link that needs auth-based visibility
 * @returns {Promise<void>}
 */
async function waitForFooter() {
  // Check if footer already exists
  if (document.querySelector('.footer')) {
    return;
  }

  // Check if we have a placeholder for dynamic footer
  const footerPlaceholder = document.getElementById('global-footer');
  if (!footerPlaceholder) {
    return;
  }

  // Wait for footer injection event (with timeout to avoid blocking)
  return Promise.race([
    new Promise(resolve => {
      window.addEventListener('layout:footer-injected', resolve, { once: true });
    }),
    new Promise(resolve => setTimeout(resolve, 500)) // 500ms timeout
  ]);
}

/**
 * Initialize auth UI
 * Call this after DOM is loaded
 */
export async function initAuthUI() {
  // Wait for header if it's dynamically injected
  await waitForHeader();

  // Phase 1: Also wait for footer (contains login link)
  await waitForFooter();

  // Cache DOM elements - support both old user-menu and new profile-widget
  userMenu = document.getElementById('user-menu');
  userMenuTrigger = document.getElementById('user-menu-trigger');
  profileWidget = document.getElementById('profile-widget');
  profileTrigger = document.getElementById('profile-trigger');
  userNameEl = document.getElementById('user-name');
  userCreditsEl = document.getElementById('user-credits');
  loginLink = document.getElementById('login-link');
  logoutBtn = document.getElementById('logout-btn');
  dashboardLinks = document.querySelectorAll('.nav-dashboard'); // All dashboard nav links
  generateLinks = document.querySelectorAll('[data-auth-required]'); // Phase 1: All auth-required links (GENERATE buttons)
  footerLoginLink = document.getElementById('footer-login-link'); // Phase 1: Footer login link

  // Phase 1: Auth elements should exist on all pages now
  // Profile widget + generate buttons are always present in header
  if (!profileWidget && (!generateLinks || generateLinks.length === 0) && !footerLoginLink) {
    console.log('[AuthUI] No auth elements found on this page');
    return;
  }

  // IMMEDIATELY apply cached state to prevent jumpy UI
  const cached = getCachedAuthState();
  if (cached && cached.isLoggedIn) {
    applyCachedUI(cached);
  }

  // Initialize auth state (async - will update UI when ready)
  await authState.init();

  // Update UI based on current state
  updateAuthUI(authState.user, authState.profile);

  // Listen for state changes
  authState.onStateChange(updateAuthUI);

  // Setup event listeners
  setupEventListeners();

  console.log('[AuthUI] Initialized');
}

// ============================================================================
// UI UPDATE
// ============================================================================

/**
 * Apply cached UI state immediately (prevents flash/jump between pages)
 * @param {Object} cached - Cached auth state
 */
function applyCachedUI(cached) {
  if (!cached || !cached.isLoggedIn) return;

  // Show profile widget immediately
  if (profileWidget) {
    profileWidget.style.display = 'flex';
  }

  // Show auth-required elements
  if (generateLinks && generateLinks.length > 0) {
    generateLinks.forEach(link => {
      const displayType = link.classList.contains('nav-link') || link.classList.contains('mobile-nav-link')
        ? 'inline-flex'
        : 'inline-block';
      link.style.display = displayType;
    });
  }

  // Hide footer login
  if (footerLoginLink) {
    footerLoginLink.style.display = 'none';
  }

  // Apply cached values
  if (cached.credits !== undefined) {
    document.querySelectorAll('.credits-value').forEach(el => {
      el.textContent = cached.credits;
    });
  }

  if (cached.displayName) {
    document.querySelectorAll('.profile-avatar').forEach(el => {
      el.textContent = cached.displayName[0].toUpperCase();
    });
  }

  console.log('[AuthUI] Applied cached state');
}

/**
 * Update auth UI based on current state
 * @param {Object} user - User object or null
 * @param {Object} profile - Profile object or null
 */
function updateAuthUI(user, profile) {
  const isLoggedIn = !!user;

  // Show/hide user menu (legacy)
  if (userMenu) {
    userMenu.style.display = isLoggedIn ? 'block' : 'none';
  }

  // Show/hide profile widget (new unified component)
  if (profileWidget) {
    profileWidget.style.display = isLoggedIn ? 'flex' : 'none';
  }

  // Show/hide login link
  if (loginLink) {
    loginLink.style.display = isLoggedIn ? 'none' : 'inline-block';
  }

  // Show/hide dashboard links (only visible when logged in)
  if (dashboardLinks && dashboardLinks.length > 0) {
    dashboardLinks.forEach(link => {
      link.style.display = isLoggedIn ? 'flex' : 'none';
    });
  }

  // Phase 1: Show/hide GENERATE buttons and other auth-required elements
  if (generateLinks && generateLinks.length > 0) {
    generateLinks.forEach(link => {
      // For nav links, use inline-flex; for buttons use inline-block
      const displayType = link.classList.contains('nav-link') || link.classList.contains('mobile-nav-link')
        ? 'inline-flex'
        : 'inline-block';
      link.style.display = isLoggedIn ? displayType : 'none';
    });
  }

  // Phase 1: Hide footer login link when logged in
  if (footerLoginLink) {
    footerLoginLink.style.display = isLoggedIn ? 'none' : 'inline-block';
  }

  // Update user info
  if (isLoggedIn && profile) {
    const displayName = profile.display_name || user.email?.split('@')[0] || 'User';
    const credits = profile.credits || 0;

    // Update all user name elements
    document.querySelectorAll('.user-name, .profile-name').forEach(el => {
      el.textContent = displayName;
    });

    // Update all credits elements
    document.querySelectorAll('.user-credits, .credits-value').forEach(el => {
      el.textContent = credits;
    });

    // Update avatar initials
    document.querySelectorAll('.profile-avatar').forEach(el => {
      el.textContent = displayName[0].toUpperCase();
    });

    // Legacy single element updates
    if (userNameEl) {
      userNameEl.textContent = displayName;
    }
    if (userCreditsEl) {
      userCreditsEl.textContent = credits;
    }

    // Cache state for instant UI on next page load
    setCachedAuthState(true, displayName, credits);
  } else {
    // Clear cache when logged out
    clearCachedAuthState();
  }

  // REFACTOR-03: Use centralized event bus
  emit(AUTH_EVENTS.USER_UPDATED, { user, profile, isLoggedIn });
}

// ============================================================================
// EVENT LISTENERS
// ============================================================================

function setupEventListeners() {
  // User menu toggle (legacy)
  if (userMenuTrigger) {
    userMenuTrigger.addEventListener('click', (e) => {
      e.stopPropagation();
      userMenu.classList.toggle('open');
    });
  }

  // Profile widget toggle (new)
  if (profileTrigger && profileWidget) {
    profileTrigger.addEventListener('click', (e) => {
      e.stopPropagation();
      profileWidget.classList.toggle('open');
    });
  }

  // Close dropdowns when clicking outside
  document.addEventListener('click', (e) => {
    if (userMenu && !userMenu.contains(e.target)) {
      userMenu.classList.remove('open');
    }
    if (profileWidget && !profileWidget.contains(e.target)) {
      profileWidget.classList.remove('open');
    }
  });

  // Close on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      userMenu?.classList.remove('open');
      profileWidget?.classList.remove('open');
    }
  });

  // Logout button
  if (logoutBtn) {
    logoutBtn.addEventListener('click', handleLogout);
  }
}

// ============================================================================
// LOGOUT HANDLER
// ============================================================================

async function handleLogout() {
  try {
    // Clear cached auth state immediately
    clearCachedAuthState();

    const { error } = await signOut();

    if (error) {
      console.error('[AuthUI] Logout error:', error);
      return;
    }

    // Redirect to home or login
    window.location.href = '/';

  } catch (error) {
    console.error('[AuthUI] Logout failed:', error);
  }
}

// ============================================================================
// CREDIT DISPLAY
// ============================================================================

/**
 * Update credit display
 * @param {number} credits - New credit count
 */
export function updateCredits(credits) {
  if (userCreditsEl) {
    userCreditsEl.textContent = credits;
  }

  // Also update auth state
  if (authState.profile) {
    authState.profile.credits = credits;
  }
}

/**
 * Show insufficient credits warning
 * @param {number} required - Credits required
 * @param {number} available - Credits available
 */
export function showInsufficientCredits(required, available) {
  // Create modal or alert
  const message = `Insufficient credits. You need ${required} credits but have ${available}. Add more credits to continue.`;

  // Simple alert for now - can be replaced with custom modal
  alert(message);
}

// ============================================================================
// AUTO-INITIALIZE
// ============================================================================

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAuthUI);
} else {
  initAuthUI();
}

// REFACTOR-03: Listen for insufficient credits via centralized event bus
subscribe(AUTH_EVENTS.CREDITS_INSUFFICIENT, ({ required, available }) => {
  showInsufficientCredits(required, available);
});
